/* eslint-disable import/order */
import {
  RouteRecordRaw,
  createRouter,
  createWebHistory,
} from 'vue-router';
import Bus, {
  NOTIFICATION,
} from '@/bus';
import {
  useStore,
} from '@/store';
import {
  CONFIG,
  CONSTANTS,
  IS_DEVELOPMENT,
  hasRouteAccess,
  redirectUserAfterLogin,
} from '@/helpers';

// Admin
const AdminTemplate = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin" */
  '@/views/Admin/AdminTemplate.vue'
);
const UserList = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-user-list" */
  '@/views/Admin/UserList.vue'
);
const MutateUser = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-mutate-user" */
  '@/views/Admin/MutateUser.vue'
);
const CompanyList = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-company-list" */
  '@/views/Admin/CompanyList.vue'
);
const MutateCompany = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-mutate-company" */
  '@/views/Admin/MutateCompany.vue'
);

const TrainingDataList = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-training-data-list" */
  '@/views/Admin/TrainingDataList.vue'
);
const MutateTrainingData = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-mutate-training-data" */
  '@/views/Admin/MutateTrainingData.vue'
);

const VisitorList = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-visitor-list" */
  '@/views/Admin/VisitorList.vue'
);

const CompanyConversationList = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "admin-conversation-list" */
  '@/views/Admin/CompanyConversationList.vue'
);

// Auth
const AuthView = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "auth" */
  '@/views/Auth/AuthView.vue'
);
const LoginView = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "auth" */
  '@/views/Auth/LoginView.vue'
);

// Individual
const UserView = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "individual/user-view" */
  '@/views/Individual/UserView.vue'
);
const AccountView = (): Promise<typeof import('*.vue')> => import(
  /* webpackChunkName: "individual/user-account-view" */
  '@/views/Individual/AccountView.vue'
);

const isGuest = {
  meta: {
    isGuest: true,
  },
};

const routes: Array<RouteRecordRaw> = [
  {
    path: '/auth',
    alias: '/',
    name: 'Auth',
    component: AuthView,
    children: [
      {
        path: 'login',
        name: 'Login',
        component: LoginView,
        meta: {
          title: 'Login',
          ...isGuest.meta,
        },
      },
    ],
  },
  {
    path: '/user',
    name: 'User',
    component: UserView,
    children: [
      {
        path: 'account/view',
        name: 'AccountView',
        component: AccountView,
        meta: {
          title: 'Account View',
        },
      },
    ],
  },
  {
    path: '/admin',
    name: 'AdminTemplate',
    component: AdminTemplate,
    children: [
      {
        path: 'user-list',
        name: 'UserList',
        component: UserList,
        meta: {
          title: 'Admin List',
        },
      },
      {
        path: 'user/add/:id',
        alias: 'user/edit/:id',
        name: 'MutateUser',
        component: MutateUser,
        meta: {
          title: 'Add/Edit Admin',
        },
      },
      {
        path: 'company-list',
        name: 'CompanyList',
        component: CompanyList,
      },
      {
        path: 'company/add/:id',
        alias: 'company/edit/:id',
        name: 'MutateCompany',
        component: MutateCompany,
        meta: {
          title: 'Add/Edit Company',
        },
      },
      {
        path: 'training-data-list',
        name: 'TrainingDataList',
        component: TrainingDataList,
      },
      {
        path: 'training-data/add/:id',
        alias: 'training-data/edit/:id',
        name: 'MutateTrainingData',
        component: MutateTrainingData,
        meta: {
          title: 'Add/Edit Training Data',
        },
      },
      {
        path: 'visitor-list',
        name: 'VisitorList',
        component: VisitorList,
      },
      {
        path: 'conversation-list',
        name: 'ConversationList',
        component: CompanyConversationList,
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  useStore.app.prevRoutePath = from.fullPath;
  useStore.app.isLoading = true;

  // ensuring user load from local storage attempted before routing
  await useStore.app.init();
  next();
});

router.afterEach((to, from, failure) => {
  useStore.app.isLoading = false;
});

router.beforeResolve((to, from, next) => {
  const { user } = useStore.auth;

  const titleSuffix = user && IS_DEVELOPMENT
    ? ` | ${user.firstName}`
    : '';
  if (!to.meta || !to.meta.title) {
    document.title = `${CONFIG.appName}${titleSuffix}`;
  } else {
    document.title = `${to.meta.title}${titleSuffix}`;
  }

  // Redirect unauthenticated users to the login page
  if (!user) {
    if (to.fullPath !== CONSTANTS.ROUTES.GUEST_HOME) {
      next(CONSTANTS.ROUTES.GUEST_HOME);
      return;
    }
    next();
    return;
  }

  const hasAccess = hasRouteAccess(to.path, user.role);
  if (!hasAccess) {
    Bus.emit(NOTIFICATION.INFO, {
      message: `Sorry, you can't access the page ${to.path}`,
    });
  }

  const shouldRedirectToMemberHome = to.meta?.isGuest || !hasAccess || to.path === '/';

  if (shouldRedirectToMemberHome) {
    next(redirectUserAfterLogin(user).route);
    return;
  }

  // Allow authenticated users to proceed
  next();
});

export { routes };

export default router;
